import React from 'react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Box,
    Image,
    Text,
    Stack,
    Button,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

import Header from './header'
import Footer from './footer'
import StoreDescription from '../store-item/store-description'
import {getUrl} from '../../../../utils/cloudinary-utils'

const StoreDetails = ({storeDetails, setStoreDetails}) => {
    const intl = useIntl()
    const googleMapUrl = `https://www.google.com/maps?hl=en&f=q&q=${storeDetails.address1}+${storeDetails.city}+${storeDetails.stateCode}+${storeDetails.zip}+United+States`

    if (!storeDetails) {
        return null
    }

    return (
        <Stack
            gap="22px"
            paddingLeft={{base: '60px', lg: '20px'}}
            paddingRight="20px"
            paddingY="20px"
            color="black"
            background={{
                base: 'white',
                md: 'gradientRightShadow'
            }}
        >
            <Button
                onClick={() => {
                    setStoreDetails(null)
                }}
                aria-label={intl.formatMessage({
                    id: 'select_store_modal.close_store_details',
                    defaultMessage: 'Close Store Details'
                })}
                variant="unstyled"
                position="fixed"
                display={{base: 'flex', lg: 'none'}}
                alignItems="center"
                justifyContent="center"
                left="0"
                top="0"
                height="100vh"
                width="31px"
                background="black.300"
            >
                <Image
                    src={getUrl('UIImages/images/back-white.svg')}
                    alt="Close Icon"
                    width="16"
                    height="13"
                />
            </Button>

            <Header storeDetails={storeDetails} setStoreDetails={setStoreDetails} />

            <Stack direction={{base: 'column', lg: 'row'}} gap="20px" alignItems="start">
                <Stack flex="1" gap="30px">
                    <Stack gap="10px">
                        <Text fontSize="16px" lineHeight="25px" letterSpacing="0.08px">
                            <FormattedMessage
                                id="select_store_modal.location"
                                defaultMessage="Location"
                            />
                        </Text>

                        <StoreDescription store={storeDetails} showName={false} showPhone={false} />

                        <Link
                            href={googleMapUrl}
                            isExternal
                            variant="underline"
                            fontSize="15px"
                            fontWeight="400"
                            textUnderlineOffset="4px"
                            lineHeight="20px"
                            letterSpacing="0.5px"
                            color="black.100"
                        >
                            <FormattedMessage
                                id="select_store_modal.get_directions"
                                defaultMessage="Get Directions"
                            />
                        </Link>
                    </Stack>

                    {(storeDetails.phone || storeDetails.email) && (
                        <Stack gap="10px">
                            <Text fontSize="16px" lineHeight="25px" letterSpacing="0.08px">
                                <FormattedMessage
                                    id="select_store_modal.contact"
                                    defaultMessage="Contact"
                                />
                            </Text>

                            <Stack
                                gap="10px"
                                fontSize="14px"
                                fontWeight="300"
                                lineHeight="22px"
                                letterSpacing="0.07px"
                            >
                                {storeDetails.phone && (
                                    <Box>
                                        <Link href={`tel:${storeDetails.phone}`}>
                                            {storeDetails.phone}
                                        </Link>
                                    </Box>
                                )}

                                {storeDetails.email && (
                                    <Box>
                                        <Link href={`mailto:${storeDetails.email}`}>
                                            {storeDetails.email}
                                        </Link>
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    )}
                </Stack>

                {storeDetails.storeHours && (
                    <Box
                        flex="1"
                        fontSize="14px"
                        fontWeight="300"
                        lineHeight="22px"
                        letterSpacing="0.07px"
                        sx={{
                            'p + p': {
                                marginTop: '10px'
                            }
                        }}
                        dangerouslySetInnerHTML={{__html: storeDetails.storeHours}}
                    />
                )}
            </Stack>

            <Footer storeDetails={storeDetails} />
        </Stack>
    )
}

StoreDetails.propTypes = {
    storeDetails: PropTypes.shape({
        name: PropTypes.string,
        city: PropTypes.string,
        address1: PropTypes.string,
        address2: PropTypes.string,
        stateCode: PropTypes.string,
        zip: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        storeHours: PropTypes.string
    }),
    setStoreDetails: PropTypes.func.isRequired
}

export default StoreDetails
